import type { CellContext } from '@tanstack/react-table';
import * as React from 'react';

import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import type MaterialCostSource from 'ab-enums/materialCostSource.enum';

import { PLATFORM_EARLIEST_DATE } from 'ab-common/constants/value';

import { Button, SegmentLabel } from '@acceligentllc/storybook';

import SimpleTable from 'af-components/Controls/SimpleTable';
import type { SimpleTableProps } from 'af-components/Controls/SimpleTable/types';
import MaterialRateSource from 'af-components/MaterialRateSource';

import styles from './styles.module.scss';

const HISTORY_COUNT_WHILE_COLLAPSED = 3;

interface RateHistoryEntry {
	id: number;
	/** YYYY-MM-DD */
	validFrom: string;
	shippingMarkup: Nullable<number>;
	salesMarkup: Nullable<number>;
	tax: Nullable<number>;
	/** ISO_DATETIME */
	updatedAt: string;
	updatedBy: string;
	isCurrent: boolean;
}

interface OwnProps<T extends RateHistoryEntry> {
	title: string;
	titleTooltip: string;
	rateSource: MaterialCostSource;
	entries: T[];
	onEdit: (item: T) => void;
}

type Props<T extends RateHistoryEntry> = OwnProps<T>;

const MaterialRatesTable = <T extends RateHistoryEntry>(props: Props<T>) => {
	const { entries, rateSource, title, titleTooltip, onEdit } = props;

	const [ratesHistoryExpanded, setRatesHistoryExpanded] = React.useState(false);

	const markupHistoriesForDisplay = React.useMemo(() => {
		const resolvedMarkupHistories = ratesHistoryExpanded ? entries : entries.slice(0, HISTORY_COUNT_WHILE_COLLAPSED);

		return resolvedMarkupHistories.map((_ch, _i) => ({
			..._ch,
			validUntil: _i === 0 ? null : entries[_i - 1].validFrom,
		}));
	}, [entries, ratesHistoryExpanded]);

	const renderIsCurrentCell = React.useCallback((cell: CellContext<(typeof markupHistoriesForDisplay)[0], boolean>) => {
		return cell.getValue() ? <b>Current</b> : '';
	}, []);

	const renderTextCell = React.useCallback((cell: CellContext<(typeof markupHistoriesForDisplay)[0], string>) => {
		return cell.getValue() ?? '-';
	}, []);

	const renderShortDateCell = React.useCallback((cell: CellContext<(typeof markupHistoriesForDisplay)[0], string>) => {
		const value = cell.getValue();
		if (!value || value === PLATFORM_EARLIEST_DATE) {
			return '-';
		}
		return TimeUtils.formatDate(value, TimeFormat.DATE_ONLY, TimeFormat.DB_DATE_ONLY);
	}, []);

	const renderPercentageCell = React.useCallback((cell: CellContext<(typeof markupHistoriesForDisplay)[0], Nullable<number>>) => {
		const value = cell.getValue();
		if (value === null) {
			return '-';
		}
		return `${value} %`;
	}, []);

	const renderLongDateCell = React.useCallback((cell: CellContext<(typeof markupHistoriesForDisplay)[0], string>) => {
		return TimeUtils.formatDate(cell.getValue(), TimeFormat.FULL_DATE_LONG, TimeFormat.ISO_DATETIME);
	}, []);

	const costHistoryColumns = React.useMemo<SimpleTableProps<(typeof markupHistoriesForDisplay)[0]>['columns']>(() => [
		{
			id: 'shippingMarkup',
			accessor: 'shippingMarkup',
			header: 'Shipping Markup (%)',
			cell: renderPercentageCell,
			enableSorting: true,
		},
		{
			id: 'salesMarkup',
			accessor: 'salesMarkup',
			header: 'Sales Markup (%)',
			cell: renderPercentageCell,
			enableSorting: true,
		},
		{
			id: 'tax',
			accessor: 'tax',
			header: 'Tax (%)',
			cell: renderPercentageCell,
			enableSorting: true,
		},
		{
			id: 'validFrom',
			accessor: 'validFrom',
			header: 'Valid From',
			cell: renderShortDateCell,
			enableSorting: true,
		},
		{
			id: 'validUntil',
			accessor: 'validUntil',
			header: 'Valid To',
			cell: renderShortDateCell,
			enableSorting: true,
		},
		{
			id: 'updatedBy',
			accessor: 'updatedBy',
			header: 'Change Made By',
			cell: renderTextCell,
			enableSorting: true,
		},
		{
			id: 'updatedAt',
			accessor: 'updatedAt',
			header: 'Change Made On',
			cell: renderLongDateCell,
		},
		{
			id: 'isCurrent',
			accessor: 'isCurrent',
			header: '',
			cell: renderIsCurrentCell,
		},
	], [renderIsCurrentCell, renderLongDateCell, renderPercentageCell, renderShortDateCell, renderTextCell]);

	const handleEditRatesClick = React.useCallback(() => {
		const [initial] = entries;
		onEdit(initial);
	}, [entries, onEdit]);

	const toggleExpand = React.useCallback(() => {
		setRatesHistoryExpanded((current) => !current);
	}, []);

	const expanderText = ratesHistoryExpanded ? 'See Less' : 'See More';
	const expanderIcon = ratesHistoryExpanded ? 'CaretUp' : 'CaretDown';

	return (
		<>
			<div className={`form-box ${styles.container}`}>
				<div className={styles.titleAndOptions}>
					<div className={styles.title}>
						<MaterialRateSource rateSource={rateSource} />
						<SegmentLabel
							label={title}
							tooltip={titleTooltip}
						/>
					</div>

					<div className={styles.options}>
						<Button
							label="Edit Rates"
							onClick={handleEditRatesClick}
							style="primary"
							type="button"
						/>
					</div>
				</div>
				<SimpleTable
					columns={costHistoryColumns}
					rows={markupHistoriesForDisplay}
				/>
				{entries.length > HISTORY_COUNT_WHILE_COLLAPSED && (
					<Button
						customStyleClass={styles.expander}
						icon={expanderIcon}
						label={expanderText}
						onClick={toggleExpand}
						style="link"
					/>
				)}
			</div>
		</>
	);
};

export default React.memo(MaterialRatesTable);
